import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Tooltip,
  TextField,
  alpha,
} from "@mui/material";
import {
  Public as PublicIcon,
  Launch as LaunchIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { CustomAutocompleteMultiSelect } from "../../../components/CustomAutocomplete";
import {
  UpdateGfMerchantDto,
  UpdateGfStoreDto,
} from "@swagger/typescript-fetch-goflyer";
import { FieldPath, FieldValue } from "../utils/fieldChangeHelpers";

interface StoreInformationProps {
  merchant?: UpdateGfMerchantDto;
  store?: UpdateGfStoreDto;
  isEditing: boolean;
  storeTagOptions: Array<{ value: any; label: string }>;
  onFieldChange: (field: FieldPath, value: FieldValue) => void;
}

export const StoreInformation: React.FC<StoreInformationProps> = ({
  merchant,
  store,
  isEditing,
  storeTagOptions,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        overflow: "visible",
      }}
    >
      <CardContent sx={{ p: 3, overflow: "visible" }}>
        {" "}
        <Box display="flex" alignItems="center" mb={3}>
          <PublicIcon sx={{ color: "#f58321", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t(messages.Store_Information())}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            {" "}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t(messages.Official_Website())}
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://example.com"
                  value={merchant?.domain || ""}
                  onChange={(e) => onFieldChange("website", e.target.value)}
                  variant="outlined"
                />
              ) : (
                <>
                  {merchant?.domain && (
                    <Button
                      component="a"
                      variant="outlined"
                      startIcon={<LaunchIcon />}
                      href={merchant?.domain}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        borderColor: "#02b7cf",
                        color: "#02b7cf",
                        "&:hover": {
                          borderColor: "#0288a3",
                          backgroundColor: alpha("#02b7cf", 0.05),
                        },
                      }}
                    >
                      {t(messages.Visit_Website())}
                    </Button>
                  )}
                  {!merchant?.domain && (
                    <Typography color="text.secondary">
                      {t(messages.Not_Available())}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            {" "}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t(messages.Google_Phone_Number())}
              </Typography>
              <Typography variant="h6" fontWeight={500}>
                {store?.phone || t(messages.Not_Available())}
              </Typography>
            </Box>
          </Grid>
          {/* Store Tags Field */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ position: "relative", zIndex: 1000 }}>
              {" "}
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t(messages.Store_Tags_Label())}
                </Typography>
                <Tooltip
                  title={t(messages.Store_tags_tooltip())}
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      ml: 0.5,
                      fontSize: 16,
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {isEditing ? (
                <CustomAutocompleteMultiSelect
                  optionsList={storeTagOptions}
                  selectedValues={
                    store?.storeTag?.map((tag) => ({
                      value: tag,
                      label: tag.name,
                    })) || []
                  }
                  handleOnChange={(value) =>
                    onFieldChange("storeTag", value || [])
                  }
                  multiSelect={true}
                  WrapperCss="width: 100%; position: relative; z-index: 1000;"
                  inputCss="width: 100%;"
                />
              ) : (
                <Typography variant="body1" fontWeight={500}>
                  {store?.storeTag && store?.storeTag.length > 0
                    ? store?.storeTag?.map((val) => val.name).join(", ")
                    : t(messages.No_tags_assigned())}
                </Typography>
              )}
            </Box>
          </Grid>
          {/* Store Signature Field */}
          <Grid item xs={12} sm={6}>
            <Box>
              {" "}
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t(messages.Store_Signature_Label())}
                </Typography>
                <Tooltip
                  title={t(messages.Store_signature_tooltip())}
                  placement="top"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      ml: 0.5,
                      fontSize: 16,
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={store?.signature || ""}
                  onChange={(e) => onFieldChange("signature", e.target.value)}
                  variant="outlined"
                  placeholder={t(
                    messages.Enter_store_signature_or_identifier()
                  )}
                />
              ) : (
                <Typography variant="body1" fontWeight={500}>
                  {store?.signature || t(messages.Not_specified())}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
